import { ChevronRightIcon } from 'components/svgs';
import { TableModel } from 'components/table/IdmTable';
import { PlannedRouteView } from 'core/graphql/CapacityPlanning/models';
import {
  formatSecondsAsHoursMinutes,
  timeSpanToHhMm,
} from 'core/utils/TimeSpanUtils';
import { routeColour } from './RouteColours';
import { recipientsText } from './perDriver';

/** One row per planned route, i.e. per driver. */
export type RouteRow = {
  colour: string;
  /**
   * Position in the depot's merged route list, which is the route's identity
   * here: a Planning Depot split into Clusters is solved several times and the
   * solver numbers each Solve's vehicles from zero again.
   */
  index: number;
  /** 1-based driver number shown to the supervisor. */
  driverNumber: number;
  stops: number;
  deliveries: number;
  recipients: number;
  distanceMeters: number;
  durationSeconds: number;
  serviceSeconds: number;
  endTime: string;
  /**
   * Share of the Shift Window this route's working day used, 0..1+, driving and
   * time spent at the stops together. Null without a shift length.
   */
  shiftUsedFraction: number | null;
};

/**
 * `shiftSeconds` is the Shift Window length (`shiftEnd - shiftStart`), so the
 * "Shift used" column can be computed; left out where it does not apply, e.g.
 * the driver dialog header, which does not show that column.
 */
export const toRouteRows = (
  routes: PlannedRouteView[],
  shiftSeconds?: number | null,
): RouteRow[] =>
  routes.map((route, index) => ({
    colour: routeColour(index),
    index,
    driverNumber: index + 1,
    stops: route.stopCount,
    deliveries: route.deliveryCount,
    recipients: route.recipients,
    distanceMeters: route.distanceMeters,
    durationSeconds: route.durationSeconds,
    serviceSeconds: route.serviceSeconds,
    endTime: timeSpanToHhMm(route.endTime),
    shiftUsedFraction:
      shiftSeconds && shiftSeconds > 0
        ? (route.durationSeconds + route.serviceSeconds) / shiftSeconds
        : null,
  }));

export const routesTableModel: TableModel<RouteRow> = {
  columns: [
    {
      title: 'Driver',
      html: (row) => (
        <span className="flex items-center justify-center gap-2">
          <span
            className="inline-block h-3 w-3 rounded-sm"
            style={{ backgroundColor: row.colour }}
          />
          {row.driverNumber}
        </span>
      ),
      width: 2,
    },
    { title: 'Stops', html: (row) => <span>{row.stops}</span>, width: 1 },
    {
      title: 'Deliveries',
      html: (row) => <span>{row.deliveries}</span>,
      width: 1,
    },
    {
      title: 'Recipients',
      html: (row) => (
        <span>{recipientsText(row.recipients, row.deliveries)}</span>
      ),
      width: 1,
    },
    {
      title: 'Distance',
      html: (row) => <span>{(row.distanceMeters / 1000).toFixed(1)} km</span>,
      width: 2,
    },
    {
      title: 'Duration',
      html: (row) => (
        <span>{formatSecondsAsHoursMinutes(row.durationSeconds)}</span>
      ),
      width: 2,
    },
    {
      title: 'Service',
      html: (row) => (
        <span>{formatSecondsAsHoursMinutes(row.serviceSeconds)}</span>
      ),
      width: 2,
    },
    {
      title: 'Shift used',
      html: (row) => (
        <span>
          {row.shiftUsedFraction === null
            ? '-'
            : `${Math.round(row.shiftUsedFraction * 100)}%`}
        </span>
      ),
      width: 2,
    },
    {
      title: 'End time',
      html: (row) => <span>{row.endTime || '-'}</span>,
      width: 2,
    },
    // Affordance only: the whole row opens the driver dialog.
    {
      title: '',
      align: 'right',
      width: 1,
      html: () => (
        <ChevronRightIcon className="h-4 w-4 text-light-black dark:text-sublte-white" />
      ),
    },
  ],
};

export default routesTableModel;
